import React, { useRef } from 'react';
import { SearchFilterContentTypes } from 'search-gateway-client';

import { ChevronDown, ChevronUp } from '@videoblocks/react-icons';

interface Option {
    label: React.ReactElement | string;
    value: string;
}

interface DropdownProps {
    options: Option[];
    value: string;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    onSelect: (value: string) => void;
    buttonClassName?: string;
    hideLabelIcon?: boolean;
}

const DEFAULT_BUTTON_CLASSES =
    'flex justify-center items-center bg-transparent z-10 whitespace-nowrap text-sm font-semibold cursor-pointer p-1 text-gray-900';

const Dropdown: React.FC<DropdownProps> = ({
    options,
    value,
    isOpen,
    onToggle,
    onClose,
    onSelect,
    buttonClassName,
    hideLabelIcon = false,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);

    return (
        <div id="search-dropdown-container" ref={dropdownRef}>
            <button
                className={buttonClassName ?? DEFAULT_BUTTON_CLASSES}
                onClick={(e) => {
                    e.preventDefault();
                    onToggle();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        onClose();
                    }
                }}
                onBlur={(e) => {
                    if (dropdownRef.current && !dropdownRef.current.contains(e.relatedTarget)) {
                        onClose();
                    }
                }}
                role="combobox"
                aria-controls="search-dropdown-options-container"
                aria-expanded={isOpen}
                aria-labelledby="search-dropdown-label-from-selection"
                type="button"
                ref={dropdownButtonRef}
            >
                <span
                    className={`flex-grow flex items-center gap-2${hideLabelIcon ? ' [&_.contentClassIcon]:hidden' : ''}`}
                >
                    <span
                        id="search-dropdown-label-from-selection"
                        className="flex-grow text-overflow-ellipsis whitespace-nowrap"
                        aria-label={`Content Type for Search: ${value}`}
                    >
                        {options.find((option) => option.value === value)?.label}
                    </span>
                    {isOpen ? (
                        <ChevronUp className="w-2 h-2 mx-1.5 fill-current" />
                    ) : (
                        <ChevronDown className="w-2 h-2 mx-1.5 fill-current" />
                    )}
                </span>
            </button>
            {isOpen && (
                <ul
                    className="absolute -ml-5 min-w-3xs block transform bg-white border rounded-3xl shadow-md z-50 border-1 border-gray-400 border-solid py-4 mt-2.5 text-sm text-gray-900 overflow-hidden"
                    role="listbox"
                    id="search-dropdown-options-container"
                    style={{ minWidth: '210px' }}
                    aria-label="Content Type Options"
                >
                    {options.map((option, index) => {
                        const isSelected = option.value === value;
                        return (
                            <li
                                className={`py-1.5 px-6 box-border cursor-pointer text-gray-900 ${isSelected ? '' : 'hover:bg-blue-100'}`}
                                key={`search-dropdown-option-${index}`}
                                id={`search-dropdown-option-${option.value}`}
                                role="option"
                                tabIndex={0}
                                aria-selected={isSelected}
                                onClick={() => {
                                    onSelect(option.value);
                                    dropdownButtonRef?.current?.focus();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        onClose();
                                    } else if (e.key === 'Enter' || e.key === ' ') {
                                        onSelect(option.value);
                                        e.preventDefault();
                                    }
                                }}
                            >
                                {option.label}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default Dropdown;
